/**
 * Waiting Room API Service (MED-804 / E3.2).
 *
 * Participant-facing endpoint backing the session waiting room page. Gated by
 * the `live_session_delivery` feature flag at the API. Each call upserts the
 * caller's presence heartbeat — the page polls this at ~4s cadence.
 */

import apiClient from './client.js';

// ============================================================================
// Types — mirror medley-api/types/waitingRoom.ts
// ============================================================================

export type WaitingRoomPresence = 'present' | 'joining' | 'not_yet_here';

export type WaitingRoomReflectionStatus =
  | 'NOT_STARTED'
  | 'IN_PROGRESS'
  | 'COMPLETED';

export type WaitingRoomSessionStatus =
  | 'unscheduled'
  | 'scheduled'
  | 'in_progress'
  | 'completed'
  | 'cancelled'
  | 'rescheduled'
  // MED-1006: terminal no-show state for a peer-led Live Session Delivery
  // session. The waiting room redirects to the dashboard when it sees this.
  | 'missed';

export type WaitingRoomSessionFormat = 'Virtual' | 'In-Person';

export interface WaitingRoomAttendee {
  user_id: string;
  display_name: string;
  initials: string;
  is_current_participant: boolean;
  role_label: 'COACH' | 'PEER LEADER' | null;
  presence: WaitingRoomPresence;
}

export interface WaitingRoomLeader {
  user_id: string | null;
  first_name: string;
  display_name: string;
  role_label: 'COACH' | 'PEER LEADER' | null;
}

export interface WaitingRoomSessionInfo {
  session_id: string;
  status: WaitingRoomSessionStatus;
  scheduled_start: string | null;
  scheduled_end: string | null;
  duration_minutes: number | null;
  timezone: string;
  format: WaitingRoomSessionFormat;
  // Direct video-call URL, surfaced as the "Join video call" CTA in the
  // waiting room (MED-804 QA fix). Null for in-person sessions.
  zoom_link: string | null;
  /**
   * MED-1112 — resolved meeting link (zoom_link → video_link → cohort default),
   * null for in-person. Preferred over `zoom_link` for the "Join video call"
   * CTA so it matches the live session views.
   */
  meeting_link: string | null;
  cohort_id: string;
  group_id: string | null;
  group_name: string | null;
}

export interface WaitingRoomReflectionState {
  status: WaitingRoomReflectionStatus;
  considered_for_prep: boolean;
}

// MED-1006 (E4.0f) — day-of no-show handoff state. Non-null only for a peer-led
// Live Session Delivery session that has entered the no-show window.
export type NoShowHeaderScenario = 'leader_not_arrived' | 'leader_declined';
export type NoShowEligibility = 'volunteered' | 'no_response' | 'passive';

export interface WaitingRoomNoShowState {
  header_scenario: NoShowHeaderScenario;
  leader_first_name: string;
  eligibility: NoShowEligibility;
  /**
   * MED-1244 AC11/AC12 — false during the confirmed-leader head start (Phase 1:
   * "Waiting for your session leader", no CTA); true once it elapses or
   * immediately when there is no confirmed leader (Phase 2: volunteer CTA).
   */
  volunteer_window_open: boolean;
  /**
   * MED-1244 AC13 — ISO timestamp of the session's estimated end. Rendered as a
   * plain "…scheduled to end at 3:00 PM" line, NOT a ticking countdown (AC10).
   * Null when no end is computable (line omitted, session never auto-missed).
   */
  session_ends_at: string | null;
  ai_features_enabled: boolean;
  session_number: number | null;
}

export interface WaitingRoomPayload {
  session: WaitingRoomSessionInfo;
  leader: WaitingRoomLeader;
  // MED-1180 — per-cohort LSD gate. Client defense-in-depth: render
  // FeaturesDisabledPage when false (backend also hard-404s the route).
  live_session_delivery_enabled: boolean;
  /** Modality-level AI gate (MED-848). Required for any AI surface. */
  ai_features_enabled: boolean;
  /**
   * Per-session AI Listening toggle (MED-876). The AI disclosure modal
   * gates on `ai_features_enabled && ai_listening_enabled` — listening
   * must actually be on for the modal copy to be accurate.
   */
  ai_listening_enabled: boolean;
  attendees: WaitingRoomAttendee[];
  reflection: WaitingRoomReflectionState;
  /** MED-1006: null in the normal "leader will start shortly" case. */
  no_show: WaitingRoomNoShowState | null;
  server_now: string;
}

/** Result of the volunteer claim-and-launch (MED-1006 AC8). */
export interface VolunteerStartResult {
  // false → another member won the race; route to the participant active view.
  became_leader: boolean;
  session_id: string;
  cohort_id: string;
}

/** MDF-242: when a participant read records presence (medley-api utils/presenceQuery). */
export type PresenceMode = 'always' | 'unless_ai_disclosure';

// ============================================================================
// API methods
// ============================================================================

export const waitingRoomApi = {
  /**
   * MDF-242: pass `presence: 'unless_ai_disclosure'` while the participant has
   * not acknowledged the AI disclosure. The server then records presence only
   * when the session needs no disclosure, in the same read.
   */
  get: (
    sessionId: string,
    opts: { presence?: PresenceMode } = {}
  ): Promise<WaitingRoomPayload> =>
    apiClient.get<WaitingRoomPayload>(
      `/api/participant/sessions/${sessionId}/waiting-room`,
      opts.presence === 'unless_ai_disclosure' ? { presence: opts.presence } : undefined
    ),

  /** MED-1006 AC8 — "I'll lead": atomic volunteer claim-and-launch. */
  volunteerStart: (sessionId: string): Promise<VolunteerStartResult> =>
    apiClient.post<VolunteerStartResult>(
      `/api/participant/sessions/${sessionId}/volunteer-start`,
      {}
    ),

  /** MED-1006 AC17 — "Keep waiting": opt out of leading for this session. */
  keepWaiting: (sessionId: string): Promise<{ opted_out: boolean }> =>
    apiClient.post<{ opted_out: boolean }>(
      `/api/participant/sessions/${sessionId}/keep-waiting`,
      {}
    ),
};
